import { useRouteParams } from '@/contexts/RouteContext';
import { useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type {
	LimitPolicyQuery,
	UpdateLimitPolicyMutation,
	UpdateLimitPolicyMutationVariables,
	TariffNameQuery
} from '@/graphql/generated';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { BILLING_CYCLES_LIST_QUERY } from '@/pages/pricing-policies/lookup.gql';
import {
	LIMIT_POLICY_QUERY,
	LIMIT_POLICY_UPDATE_MUTATION,
	LIMIT_POLICIES_LIST_QUERY
} from './limit-policies.gql.tsx';
import { LimitPolicyForm } from './limit-policy-form';
import {
	buildUpdateLimitPolicyInput,
	limitPolicyFormSchema,
	type LimitPolicyFormValues
} from './limit-policy-input';
import { TARIFF_NAME_QUERY } from '../tariffs/tariffs.gql.tsx';

const isValidUlid = (value: string | undefined): value is string => {
	if (!value) {
		return false;
	}

	return /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/i.test(value);
};

export const LimitPoliciesEdit = () => {
	const { id } = useRouteParams();
	const go = useGo();
	useEditPageHeader({ id, resourceName: 'limit-policies' });

	const {
		data: policyData,
		loading: loadingPolicy,
		error: policyError
	} = useQuery<LimitPolicyQuery>(LIMIT_POLICY_QUERY, {
		variables: { id: id ?? '' },
		skip: !isValidUlid(id)
	});

	const policy = policyData?.limitPolicy ?? null;

	const memoizedInitialValues = useMemo<
		LimitPolicyFormValues | undefined
	>(() => {
		if (!policy) {
			return undefined;
		}

		return {
			name: policy.name ?? '',
			priority: policy.priority != null ? String(policy.priority) : '',
			isActive: Boolean(policy.isActive),
			tariffId: policy.tariffId ?? '',
			scenarioId: policy.scenario?.scenarioId ?? '',
			limit: {
				limitId: policy.limit?.limitId ?? undefined,
				minAmount: policy.limit?.minAmount ?? undefined,
				maxAmount: policy.limit?.maxAmount ?? undefined,
				minStayMinutes: policy.limit?.minStayMinutes ?? undefined,
				maxStayMinutes: policy.limit?.maxStayMinutes ?? undefined
			},
			billingCycleId:
				policy.billingCycle?.billingCycleId != null
					? String(policy.billingCycle.billingCycleId)
					: ''
		};
	}, [policy]);

	const initialScenarioLabel = policy?.scenario?.name ?? undefined;

	// Fetch tariff name for the autocomplete initial label (no navigation property)
	const { data: tariffData } = useQuery<TariffNameQuery>(TARIFF_NAME_QUERY, {
		variables: { id: policy?.tariffId ?? '' },
		skip: !policy?.tariffId
	});

	const initialTariffLabel = tariffData?.tariff?.name;

	const form = useEntityForm<LimitPolicyFormValues>({
		mode: 'edit',
		schema: limitPolicyFormSchema,
		defaultValues: {
			name: '',
			priority: '',
			isActive: true,
			tariffId: '',
			scenarioId: '',
			limit: {},
			billingCycleId: ''
		},
		initialData: memoizedInitialValues
	});

	const {
		data: billingCyclesData,
		loading: loadingBillingCycles,
		error: billingCyclesError
	} = useQuery(BILLING_CYCLES_LIST_QUERY);

	const { data: limitPoliciesData } = useQuery(LIMIT_POLICIES_LIST_QUERY, {
		variables: { first: 100 }
	});

	const lookupsLoading = loadingBillingCycles;
	const lookupError = billingCyclesError;

	const { shouldShowLoading } = useDelayedLoading(lookupsLoading, {
		delay: 300,
		showInitialLoading: false
	});

	const billingCycles = billingCyclesData?.billingCycles ?? [];

	const existingPriorities = useMemo(() => {
		const nodes = limitPoliciesData?.limitPolicies?.nodes ?? [];
		return nodes
			.filter((node: any) => node.priority != null && node.limitPolicyId !== id)
			.map((node: any) => ({
				priority: node.priority,
				label: node.name
			}));
	}, [limitPoliciesData, id]);

	const [updateLimitPolicy, { loading: isSubmitting }] = useMutation<
		UpdateLimitPolicyMutation,
		UpdateLimitPolicyMutationVariables
	>(LIMIT_POLICY_UPDATE_MUTATION);

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	const handleSubmit = async (values: LimitPolicyFormValues) => {
		if (!policy?.limitPolicyId) {
			showMutationErrorToast('limit-policies', 'update', { message: 'Limit policy identifier is missing.' });
			return;
		}

		let input;

		try {
			input = buildUpdateLimitPolicyInput(values, policy.limitPolicyId);
		} catch (error) {
			showMutationErrorToast('limit-policies', 'update', { error });
			return;
		}

		try {
			const { data } = await updateLimitPolicy(
				getMutationOptions('limit-policies', { input })
			);

			const message = extractCheckResultError(data?.updateLimitPolicy);

			if (message) {
				showMutationErrorToast('limit-policies', 'update', { message });
				return;
			}

			showMutationSuccessToast('limit-policies', 'update');
			go({
				to: '/limit-policies',
				state: { selectLimitPolicyId: policy.limitPolicyId }
			});
		} catch (error) {
			showMutationErrorToast('limit-policies', 'update', { error });
		}
	};

	if (!id) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Limit policy not found</AlertTitle>
					<AlertDescription>
						A limit policy identifier was not provided in the URL.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (!isValidUlid(id)) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="max-w-md sentry-card">
					<div className="space-y-4 text-center sentry-card-body">
						<div className="text-lg font-medium text-red-500">
							Invalid Limit Policy ID
						</div>
						<p className="text-gray-600">
							The limit policy ID format is invalid:{' '}
							<code className="px-2 py-1 bg-gray-100 rounded">
								{id}
							</code>
						</p>
						<p className="text-sm text-gray-500">
							Expected a valid ULID (26 character alphanumeric
							string)
						</p>
						<button
							onClick={() => go({ to: '/limit-policies' })}
							className="sentry-btn-primary"
						>
							Back to Limit Policies
						</button>
					</div>
				</div>
			</div>
		);
	}

	if (loadingPolicy) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button
						variant="outline"
						onClick={() => go({ to: '/limit-policies' })}
					>
						← Back
					</Button>
					<h1 className="text-2xl font-bold">Edit Limit Policy</h1>
				</div>

				<Card className="text-sm text-muted-foreground">
					Loading limit policy details...
				</Card>
			</div>
		);
	}

	if (policyError || !policy) {
		return (
			<div className="p-4">
				<Alert variant="destructive">
					<AlertTitle>Unable to load limit policy</AlertTitle>
					<AlertDescription>
						{policyError?.message ??
							'The requested limit policy could not be found.'}
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	return (
		<FormPageLayout>
			{lookupError && !shouldShowLoading && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load form data</AlertTitle>
					<AlertDescription>
						{lookupError.message ??
							'Some lookup values could not be loaded.'}
					</AlertDescription>
				</Alert>
			)}

			{shouldShowLoading ? (
				<Card>
					<div className="text-sm text-muted-foreground">
						Loading form data...
					</div>
				</Card>
			) : (
				<LimitPolicyForm
					mode="edit"
					form={form}
					billingCycles={billingCycles}
					existingPriorities={existingPriorities}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
					initialScenarioLabel={initialScenarioLabel}
					initialTariffLabel={initialTariffLabel}
					headerTitle="Edit Limit Policy"
				/>
			)}
		</FormPageLayout>
	);
};
